import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { MONTH_NAMES, formatPeriod } from "@/lib/resumeUtils";
import WizardModal from "./WizardModal";
import ThinkingLoader from "@/components/ThinkingLoader";
import { ArrowLeft, ArrowRight, Check, Sparkles, FileText, FileUp, Layers, FilePlus2, User, UserX, Briefcase, Palette } from "lucide-react";
import { toast } from "sonner";

const BASES = [
  { key: "existing", labelKey: "ncwBaseExisting", hintKey: "ncwBaseExistingHint", Icon: Layers },
  { key: "new", labelKey: "ncwBaseNew", hintKey: "ncwBaseNewHint", Icon: FilePlus2 },
  { key: "central", labelKey: "ncwBaseCentral", hintKey: "ncwBaseCentralHint", Icon: FileText },
  { key: "file", labelKey: "ncwBaseFile", hintKey: "ncwBaseFileHint", Icon: FileUp },
];

function OptionCard({ selected, onClick, Icon, label, hint, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left ed-card p-4 flex items-start gap-4 transition-colors ${selected ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
    >
      <Icon size={17} strokeWidth={1.5} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="font-serif text-lg block leading-tight">{label}</span>
        <span className="text-xs text-neutral-600 block mt-1.5 leading-relaxed">{hint}</span>
      </span>
      {selected && <Check size={16} strokeWidth={2} className="ml-auto shrink-0" />}
    </button>
  );
}

export default function NewCvWizard({ variants, onClose, onCreated, onLaunchJobWizard }) {
  const { t, lang } = useT();
  const [step, setStep] = useState(1);
  const [base, setBase] = useState(null);
  const [baseId, setBaseId] = useState(null);
  const [baseDetail, setBaseDetail] = useState(null);
  const [file, setFile] = useState(null);
  const [takeAll, setTakeAll] = useState(true);
  const [selExp, setSelExp] = useState([]);
  const [selSkills, setSelSkills] = useState([]);
  const [showPersonal, setShowPersonal] = useState(true);
  const [cvTitle, setCvTitle] = useState("");
  const [saveName, setSaveName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  // Track whether the user has a "standaard CV" set — either an uploaded
  // PDF (via /api/cv) or a variant flagged as their central CV. The
  // "Uit mijn standaard CV" wizard option is hidden unless one exists,
  // since importing without a source would just error.
  const [hasCentralCv, setHasCentralCv] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/cv-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    // Resolve whether the user has any form of "standaard CV" available.
    Promise.all([
      api.get("/cv").catch(() => ({ data: { present: false } })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]).then(([cv, me]) => {
      setHasCentralCv(!!cv?.data?.present || !!me?.data?.central_uploaded_cv);
    });
  }, []);

  const availableBases = BASES.filter((b) => b.key !== "central" || hasCentralCv);

  const pickBaseVariant = async (id) => {
    setBaseId(id);
    try {
      const { data } = await api.get(`/resumes/${id}`);
      setBaseDetail(data);
      setSelExp((data.experiences || []).map((e) => e.id));
      setSelSkills((data.skills || []).map((s) => s.name));
    } catch {
      setBaseDetail(null);
    }
  };

  // Default save name: "{Titel} CV_{maand-jaar}_{count}"
  useEffect(() => {
    if (step === 5 && !nameTouched) {
      const now = new Date();
      const month = (MONTH_NAMES[lang] || MONTH_NAMES.nl)[now.getMonth()];
      const count = (variants?.length || 0) + 1;
      const prefix = cvTitle.trim() || "CV";
      setSaveName(`${prefix} CV_${month} ${now.getFullYear()}_${count}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const next = () => {
    if (step === 1) {
      if (!base) return;
      if (base === "existing" && !baseId) { toast.error(t("ncwPickBaseFirst")); return; }
      if (base === "file" && !file) { toast.error(t("ncwPickFileFirst")); return; }
      setStep(base === "existing" ? 2 : 3);
    } else if (step === 4) {
      if (!cvTitle.trim()) { toast.error(t("ncwTitleRequired")); return; }
      setStep(5);
    } else {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (step === 3) setStep(base === "existing" ? 2 : 1);
    else setStep(step - 1);
  };

  const create = async () => {
    const name = saveName.trim();
    if (!name) { toast.error(t("rbNameRequired")); return; }
    setCreating(true);
    try {
      let data;
      if (base === "central") {
        ({ data } = await api.post("/resumes/import", {
          name, title: cvTitle.trim(), show_personal: showPersonal,
          template_id: templateId || "",
        }));
      } else if (base === "file") {
        const fd = new FormData();
        fd.append("name", name);
        fd.append("title", cvTitle.trim());
        fd.append("show_personal", showPersonal);
        fd.append("template_id", templateId || "");
        fd.append("file", file);
        ({ data } = await api.post("/resumes/import-file", fd));
      } else {
        ({ data } = await api.post("/resumes", {
          name, title: cvTitle.trim(), show_personal: showPersonal,
          template_id: templateId || "",
          base_resume_id: base === "existing" ? baseId : "",
          take_all: takeAll,
          include_experience_ids: takeAll ? [] : selExp,
          include_skills: takeAll ? [] : selSkills,
        }));
      }
      setCreated(data);
      onCreated(data);
      toast.success(t("rbCreated"));
      setStep(6);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rbImportFailed"));
    } finally {
      setCreating(false);
    }
  };

  const stepTitle = [t("ncwStep1"), t("ncwStep2"), t("ncwStep3"), t("ncwStep4"), t("ncwStep5"), t("ncwDone")][step - 1];

  return (
    <WizardModal testId="newcv-wizard" title={t("ncwTitle")} onClose={onClose} wide>
      <p className="font-serif text-xl italic text-neutral-800 mb-5">{stepTitle}</p>

      {step === 1 && (
        <div className="space-y-3">
          {availableBases.map(({ key, labelKey, hintKey, Icon }) => (
            <OptionCard
              key={key}
              testId={`newcv-base-${key}`}
              selected={base === key}
              Icon={Icon}
              label={t(labelKey)}
              hint={key === "file" && file ? file.name : t(hintKey)}
              onClick={() => {
                setBase(key);
                if (key === "file") fileRef.current?.click();
              }}
            />
          ))}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            data-testid="newcv-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {base === "existing" && (
            <div className="pt-3 space-y-2">
              <p className="ed-label">{t("ncwPickBase")}</p>
              {variants.length === 0 ? (
                <p className="text-sm text-neutral-500 italic font-serif">{t("rbNoVariants")}</p>
              ) : variants.map((v, i) => (
                <button
                  key={v.id}
                  data-testid={`newcv-basevariant-${i}`}
                  onClick={() => pickBaseVariant(v.id)}
                  className={`w-full text-left ed-card px-4 py-3 flex items-center justify-between gap-3 transition-colors ${baseId === v.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                >
                  <span className="font-serif text-base truncate">{v.name}</span>
                  {baseId === v.id && <Check size={15} strokeWidth={2} className="shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <OptionCard testId="newcv-take-all" selected={takeAll} Icon={Layers}
            label={t("ncwTakeAll")} hint={t("ncwTakeAllHint")} onClick={() => setTakeAll(true)} />
          <OptionCard testId="newcv-take-some" selected={!takeAll} Icon={Briefcase}
            label={t("ncwTakeSome")} hint={t("ncwTakeSomeHint")} onClick={() => setTakeAll(false)} />

          {!takeAll && baseDetail && (
            <div className="pt-4 space-y-5">
              <div>
                <p className="ed-label mb-2">{t("ncwSelectExp")}</p>
                <div className="space-y-2">
                  {(baseDetail.experiences || []).map((e, i) => (
                    <label
                      key={e.id}
                      data-testid={`newcv-exp-${i}`}
                      className={`ed-card px-4 py-3 flex items-start gap-3 cursor-pointer transition-colors ${selExp.includes(e.id) ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                    >
                      <input type="checkbox" className="mt-1 w-4 h-4 accent-neutral-900"
                        checked={selExp.includes(e.id)} onChange={() => toggle(selExp, setSelExp, e.id)} />
                      <span className="min-w-0">
                        <span className="font-serif text-base block leading-tight">
                          {e.role || "—"}{e.company ? <span className="italic text-neutral-600"> · {e.company}</span> : null}
                        </span>
                        <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
                          {formatPeriod(e, lang, lang === "en" ? "present" : "heden")}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="ed-label mb-2">{t("ncwSelectSkills")}</p>
                <div className="flex flex-wrap gap-2">
                  {(baseDetail.skills || []).map((s, i) => (
                    <button
                      key={s.name}
                      data-testid={`newcv-skill-${i}`}
                      onClick={() => toggle(selSkills, setSelSkills, s.name)}
                      className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${selSkills.includes(s.name) ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"}`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <OptionCard testId="newcv-show-full" selected={showPersonal} Icon={User}
            label={t("ncwShowFull")} hint={t("ncwShowFullHint")} onClick={() => setShowPersonal(true)} />
          <OptionCard testId="newcv-show-name" selected={!showPersonal} Icon={UserX}
            label={t("ncwShowNameOnly")} hint={t("ncwShowNameOnlyHint")} onClick={() => setShowPersonal(false)} />
        </div>
      )}

      {step === 4 && (
        <div>
          <label className="ed-label">{t("ncwCvTitleLabel")}</label>
          <input
            data-testid="newcv-title"
            className="ed-input max-w-md"
            placeholder={t("ncwCvTitlePh")}
            value={cvTitle}
            onChange={(e) => setCvTitle(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {step === 5 && (
        <div className="space-y-5">
          <div>
            <label className="ed-label">{t("ncwSaveNameLabel")}</label>
            <input
              data-testid="newcv-savename"
              className="ed-input max-w-lg"
              value={saveName}
              onChange={(e) => { setSaveName(e.target.value); setNameTouched(true); }}
            />
          </div>
          {templates.length > 0 && (
            <div>
              <label className="ed-label mb-2 flex items-center gap-1.5">
                <Palette size={12} strokeWidth={1.5} /> {t("ncwTemplateLabel")}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  data-testid="newcv-template-default"
                  onClick={() => setTemplateId(null)}
                  className={`ed-card px-4 py-3 text-left flex items-center gap-3 transition-colors ${!templateId ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                >
                  <span className="w-5 h-5 shrink-0 border border-neutral-300 bg-[#1A1A1A]" />
                  <span className="font-serif text-base truncate flex-1">{t("tplDefaultOption")}</span>
                  {!templateId && <Check size={14} strokeWidth={2} className="shrink-0" />}
                </button>
                {templates.map((tpl, i) => (
                  <button
                    key={tpl.id}
                    data-testid={`newcv-template-${i}`}
                    onClick={() => setTemplateId(tpl.id)}
                    className={`ed-card px-4 py-3 text-left flex items-center gap-3 transition-colors ${templateId === tpl.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
                  >
                    <span className="w-5 h-5 shrink-0 border border-neutral-300" style={{ background: tpl.design?.accent || "#1A1A1A" }} />
                    <span className="font-serif text-base truncate flex-1">{tpl.name}</span>
                    {templateId === tpl.id && <Check size={14} strokeWidth={2} className="shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}
          {creating && (
            <div className="space-y-2" data-testid="newcv-creating-wrapper">
              <p className="ed-eyebrow flex items-center gap-2" data-testid="newcv-creating">
                <Sparkles size={12} strokeWidth={1.5} className="animate-pulse" /> {t("ncwCreating")}
              </p>
              <ThinkingLoader stage="resume" testId="newcv-thinking" />
            </div>
          )}
        </div>
      )}

      {step === 6 && created && (
        <div className="space-y-6">
          <div className="ed-card p-6">
            <p className="font-serif text-2xl leading-tight">{created.name}</p>
            <p className="font-serif text-lg italic text-neutral-600 mt-1">{created.personal?.title || ""}</p>
          </div>
          <p className="text-sm text-neutral-600">{t("ncwCreatedMsg")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button data-testid="newcv-launch-job" className="ed-btn justify-center" onClick={onLaunchJobWizard}>
              <Briefcase size={14} strokeWidth={1.5} /> {t("ncwLaunchJob")}
            </button>
            <button data-testid="newcv-finish" className="ed-btn ed-btn-outline justify-center" onClick={onClose}>
              {t("ncwFinish")}
            </button>
          </div>
        </div>
      )}

      {step < 6 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-200">
          {step > 1 ? (
            <button data-testid="newcv-back" className="ed-btn ed-btn-outline" onClick={back} disabled={creating}>
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
            </button>
          ) : <span />}
          {step < 5 ? (
            <button data-testid="newcv-next" className="ed-btn" onClick={next} disabled={step === 1 && !base}>
              {t("wizNext")} <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          ) : (
            <button data-testid="newcv-create" className="ed-btn" onClick={create} disabled={creating}>
              <Check size={14} strokeWidth={1.5} /> {creating ? t("ncwCreating") : t("ncwCreate")}
            </button>
          )}
        </div>
      )}
    </WizardModal>
  );
}
